import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { grammarService } from '../../../services/grammarService';

interface GrammarStatsProps {
  onError: (error: string | null) => void;
}

interface Analytics {
  totalFeatures: number;
  byCategory: Array<{ category: string; count: number; }>;
  byCefrIntroduction: Array<{ level: string; count: number; }>;
  byCefrMastery: Array<{ level: string; count: number; }>;
  byComplexity: Array<{ score: string; count: number; }>;
}

const GrammarStats: React.FC<GrammarStatsProps> = ({ onError }) => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        onError(null);
        const data = await grammarService.getAnalytics();
        setAnalytics(data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
        onError(error instanceof Error ? error.message : 'Failed to fetch grammar analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [onError]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Row: Total and CEFR Introduction Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Total Features Card */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Total Features</h3>
          <p className="text-3xl font-bold text-talaera-blue">
            {analytics?.totalFeatures ?? '-'}
          </p>
        </div>

        {/* CEFR Introduction Distribution Chart */}
        <div className="lg:col-span-3 bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Features by CEFR Introduction Level</h3>
          <div className="h-64">
            {analytics && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.byCefrIntroduction}>
                  <XAxis dataKey="level" />
                  <YAxis />
                  <Tooltip />
                  <Bar 
                    dataKey="count" 
                    fill="#01bcd4"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Second Row: CEFR Mastery and Complexity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CEFR Mastery Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Features by CEFR Mastery Level</h3>
          <div className="h-64">
            {analytics && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.byCefrMastery}>
                  <XAxis dataKey="level" />
                  <YAxis />
                  <Tooltip />
                  <Bar 
                    dataKey="count" 
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Complexity Distribution Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Features by Complexity Score</h3>
          <div className="h-64">
            {analytics && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.byComplexity}>
                  <XAxis dataKey="score" />
                  <YAxis />
                  <Tooltip />
                  <Bar 
                    dataKey="count" 
                    fill="#f59e0b"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row: Category Table */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Features by Category</h3>
        <div className="overflow-y-auto max-h-[400px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics?.byCategory.map((item) => (
                <tr key={item.category} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {item.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GrammarStats; 